import { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '@store';
import { Account, PermissionEnum } from '@ui/src';
import {
  useLazyGetCustomerActiveLocksQuery,
  useLazyGetIndividualUserQuery,
  useLazyGetMainChannelQuery,
  useLazyGetIndividualAccountBalanceQuery,
} from '../../store/services/individual';
import { useLazyGetCustomersInfoDictionariesQuery } from '../../../../store/dictionaries/services';
import {
  useLazyGetCustomersTopUpQuery,
  useLazyGetCustomerUserScoreQuery,
} from '../../../customers/store/services/customers';
import {
  clearGeneralState,
  setBalances,
  setConvertedToIndividual,
} from '../../store/slices/general-info';
import { Balance, CustomerScore } from '../../../common/types';
import { clearAccountsState } from '../../../common/store/slices/accounts';
import { clearStatisticState } from '../../../common/store/slices/statistics';
import { clearDocumentsState } from '../../../common/store/slices/documents/documents';
import { clearTariffsState } from '../../../common/store/slices/tariff/tariff';
import { clearDevicesState } from '../../store/slices/devices';
import { clearExternalAccounts } from '../../../common/store/slices/external-integration';
import {
  clearLocksState,
  clearLogsState,
  setOrganizationId,
} from '../../store/slices/security';
import {
  clearAccountsTransactionState,
  clearCardsTransactionState,
} from '../../../common/store/slices/statements';
import { clearOverviewState } from '../../store/slices/overview';
import { clearTopUpOptions } from '../../../common/store/slices/top-up-panel';
import { clearAmlState } from '../../../common/store/slices/aml';
import { clearBeneficiariesState } from '../../../common/store/slices/beneficiaries';

const useInformation = ({ customerId }: { customerId: string }) => {
  const [query, setQuery] = useState<string | undefined>(undefined);
  const [notFound, setNotFound] = useState(false);

  const { baseInformation, convertedToIndividual } = useAppSelector(
    (state) => state.individualCustomers.info.general,
  );
  const { topUpOptions } = useAppSelector((state) => state.topUpPanel);

  const { activePermissions } = useAppSelector((state) => state.root);

  const { search } = useLocation();

  const dispatch = useAppDispatch();

  const [getUser, { isLoading, isFetching }] = useLazyGetIndividualUserQuery();
  const [getCustomersTopUp] = useLazyGetCustomersTopUpQuery();
  const [getMainChannel] = useLazyGetMainChannelQuery();
  const [getCustomerActiveLock] = useLazyGetCustomerActiveLocksQuery();
  const [getIndividualScore] = useLazyGetCustomerUserScoreQuery();
  const [getInfoDictionaries, { isLoading: dictionariesIsLoading }] =
    useLazyGetCustomersInfoDictionariesQuery();
  const [getBalance] = useLazyGetIndividualAccountBalanceQuery();

  const getBalancesAction = useCallback(async (accounts?: Account[]) => {
    if (accounts && accounts.length) {
      const balancesResponse = await Promise.all(
        accounts.map(async (item) => {
          const response = await getBalance(item.id);

          return response?.data?.data;
        }),
      );

      const formatted: Balance[] = [];

      balancesResponse.forEach((item) => {
        item?.edges.forEach((balance) => {
          formatted.push(balance.node);
        });
      });

      dispatch(
        setBalances({
          balances: formatted,
          totalBCBalances: (accounts
            ? accounts.map((item) => item.totalBalanceBC)
            : [0]) as number[],
        }),
      );
    }
  }, []);

  const onClear = () => {
    dispatch(clearGeneralState());
    dispatch(clearOverviewState());
    dispatch(clearAccountsTransactionState());
    dispatch(clearCardsTransactionState());
    dispatch(clearAccountsState());
    dispatch(clearStatisticState());
    dispatch(clearDocumentsState());
    dispatch(clearTariffsState());
    dispatch(clearAmlState());
    dispatch(clearExternalAccounts());
    dispatch(clearDevicesState());
    dispatch(clearLocksState());
    dispatch(clearLogsState());
    dispatch(setOrganizationId(''));
    dispatch(clearBeneficiariesState());
    dispatch(clearTopUpOptions());
  };

  const getUserAction = useCallback(async () => {
    const { error } = (await getUser(customerId)) as {
      data: any;
      error: any;
    };
    const amlUpdateId = localStorage.getItem('amlUpdateId');

    if (error?.data) {
      dispatch(setConvertedToIndividual(false));
      if (error.data.statusCode === 400) {
        setNotFound(true);
      }
    } else if (
      !error &&
      !amlUpdateId &&
      activePermissions?.includes(
        PermissionEnum.INDIVIDUAL_CUSTOMERS_SCORE_LIST,
      )
    ) {
      (await getIndividualScore(customerId)) as {
        data: { data: CustomerScore };
      };
    }
  }, []);

  useEffect(() => {
    if (baseInformation) {
      getBalancesAction(baseInformation?.accounts);

      if (!topUpOptions) {
        getCustomersTopUp({});
      }
    }
  }, [baseInformation, topUpOptions]);

  useEffect(() => {
    if (customerId) {
      getUserAction();
      getMainChannel(customerId);
    }
  }, [customerId]);

  useEffect(() => {
    if (query === undefined) {
      setQuery(search);
    }
  }, [query, search]);

  useEffect(() => {
    if (baseInformation) {
      getCustomerActiveLock(baseInformation?.userId as string);
    }
  }, [baseInformation]);

  useEffect(() => {
    if (convertedToIndividual) {
      getUserAction();
    }
  }, [convertedToIndividual]);

  useEffect(() => {
    if (
      activePermissions?.includes(
        PermissionEnum.INDIVIDUAL_CUSTOMERS_EDIT_DETAILS_ACTION,
      )
    ) {
      getInfoDictionaries();
    }
  }, [activePermissions]);

  useEffect(
    () => () => {
      onClear();
    },
    [dispatch],
  );

  return {
    isLoading,
    isFetching,
    dictionariesIsLoading,
    notFound,
    query,
    onClear,
  };
};

export default useInformation;
